import {
  ICoordinateSystemInfoProvider,
  SCENE_COMMON_TOKEN,
  SceneCommon,
  SceneFactory,
  T_ICoordinateSystemInfoProvider,
} from '@cgs/common';
import {
  HelpPopupComponent,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  ScrollableProgressBarComponent,
} from '@cgs/machines';
import {
  Button,
  Compatibility,
  Container,
  NinePatchSceneObject,
  PIXEL_RATIO,
  Platform,
  ProgressBar,
  SceneObject,
  SoundSceneObject,
  T_Platform,
  TemplateApplication,
  TextSceneObject,
  TtfTextSceneObject,
  Vector2,
  View,
} from '@cgs/wge';
import { TrifectaHudZIndex } from './trifecta-hud-z-index';

const enum SceneNames {
  GameRulesPopup = 'gameRulesPopup',
  CloseButton = 'gamerulesPanelBtnClose',
  PopupOrientation = 'popupOrientation',
  BackDropArea = 'gameRulesAreaBtn',
  BackgroundImage = 'bg',
  ClickSound = 'button_click',
}

const enum StateNames {
  ShowPopup = 'gameRulesPopup_show',
  HidePopup = 'gameRulesPopup_hide',
  Default = 'gameRulesPopup_default',
}

export class TrifectaGameHelp extends HelpPopupComponent {
  private gameRulesPopup: SceneObject;

  private popupOrientation: SceneObject;

  private scrollableProgressBar: ScrollableProgressBarComponent;
  private readonly sceneFactory: SceneFactory;

  private gameHelpBackgroundOriginalSize: Vector2;
  private closeButtonOriginalPosition: Vector2;

  constructor(container: Container) {
    super();

    this.sceneFactory = container.resolve<SceneCommon>(SCENE_COMMON_TOKEN)!.sceneFactory;

    const resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    const scene = resourcesComponent.root;

    const soundClickScene = resourcesComponent.sounds
      .findById<SoundSceneObject>(SceneNames.ClickSound)
      ?.findAllByType(SoundSceneObject)[0];

    const gameRulesScenePopup = this.sceneFactory.build('bottom/sceneHelp');
    if (!gameRulesScenePopup) {
      throw new Error('game rules scene popup not found');
    }

    const gameRulesPopup = gameRulesScenePopup.findById<SceneObject>(SceneNames.GameRulesPopup);

    if (!gameRulesPopup) {
      throw new Error('game rules popup not found');
    }

    const backDropArea = gameRulesPopup.findById<Button>(SceneNames.BackDropArea);

    if (!backDropArea) {
      throw new Error('back drop area not found');
    }

    const popupOrientation = gameRulesPopup.findById<SceneObject>(SceneNames.PopupOrientation);

    if (!popupOrientation) {
      throw new Error('popup orientation not found');
    }

    this.popupOrientation = popupOrientation;

    const gameHelpBackground = gameRulesScenePopup.findById<NinePatchSceneObject>(
      SceneNames.BackgroundImage
    );

    if (!gameHelpBackground) {
      throw new Error('game rules background not found');
    }

    const closeButton = gameRulesPopup.findById<Button>(SceneNames.CloseButton);

    if (!gameRulesPopup) {
      throw new Error('game rules popup not found');
    }

    if (!closeButton) {
      throw new Error('close button not found');
    }
    this.closeButtonOriginalPosition = closeButton.position.clone();
    this.gameHelpBackgroundOriginalSize = gameHelpBackground.size.clone();

    closeButton.clicked.listen(() => {
      soundClickScene?.stop();
      soundClickScene?.play();
      this.hide();
    });

    backDropArea.clicked.listen(() => {
      soundClickScene?.stop();
      soundClickScene?.play();
      this.hide();
    });

    gameRulesScenePopup.z = TrifectaHudZIndex.GameSettingsPopup;

    scene.addChild(gameRulesScenePopup);

    this.gameRulesPopup = gameRulesPopup;
    this.initGameRules(gameRulesPopup);

    gameRulesScenePopup.initialize();

    const platform = container.resolve<Platform>(T_Platform);

    const coordinateSystem = container.resolve<ICoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    );

    platform.view.sizeChanged.listen(() => {
      this.handleOrientationChange();
      this.updateGameRulesBackgroundSize(
        gameHelpBackground,
        gameRulesPopup,
        coordinateSystem,
        closeButton,
        platform.view
      );
    });

    this.hideGameLines();

    this.updateGameRulesBackgroundSize(
      gameHelpBackground,
      gameRulesPopup,
      coordinateSystem,
      closeButton,
      platform.view
    );
  }

  public show(): void {
    this.handleOrientationChange();
    this.scrollableProgressBar.resetProgress();
    this.gameRulesPopup.stateMachine?.switchToState(StateNames.ShowPopup);
    this.isShown.emit(true);
  }

  public hide(): void {
    this.gameRulesPopup.stateMachine?.switchToState(StateNames.HidePopup);
    this.isShown.emit(false);
  }

  private updateGameRulesBackgroundSize(
    gameRulesBackground: NinePatchSceneObject,
    gameRulesPopup: SceneObject,
    coordinateSystem: ICoordinateSystemInfoProvider,
    closeButton: Button,
    view: View
  ): void {
    gameRulesBackground.size = new Vector2(view.width, view.height).mulNum(PIXEL_RATIO);

    const maskSceneObject = gameRulesPopup.findById<SceneObject>('maskid')!;
    const contentSize = gameRulesPopup.findById<SceneObject>('contentSize')!;
    const rulesHolder = gameRulesPopup.findById<SceneObject>('rulesHolder')!;
    const contentScale = this.scrollableProgressBar.scene.childs[0].scale;

    setTimeout(() => {
      const finalScale = TemplateApplication.scale.clone().multiply(gameRulesPopup.scale);
      const finalSize = coordinateSystem.coordinateSystem.size.clone().divide(finalScale);

      const sizeDiff = finalSize.clone().subtract(this.gameHelpBackgroundOriginalSize);

      gameRulesBackground.size = finalSize;
      const currentSize = maskSceneObject.mask.size.y * maskSceneObject.mask.scale.y;
      const maskSizeDiff = finalSize.y - currentSize - 320;
      maskSceneObject.mask.scale = maskSceneObject.mask.scale
        .clone()
        .multiply(new Vector2(1, 1 + maskSizeDiff / currentSize));

      closeButton.position = this.closeButtonOriginalPosition
        .clone()
        .add(new Vector2(sizeDiff.x, 0));

      const contentWidth = contentSize.bounds.width * contentScale.x;
      const finalSizeWidth = finalSize.x - 100;

      const fitScale = Math.min(finalSizeWidth / contentWidth, 1);

      rulesHolder.scale = new Vector2(fitScale, fitScale);
      const maskHeight =
        maskSceneObject.mask.size.y *
        maskSceneObject.mask.scale.y *
        this.gameRulesPopup.scale.y *
        fitScale *
        0.5;
      this.calculateHeight(this.scrollableProgressBar.scene, maskHeight);
    }, 0);
  }

  private handleOrientationChange(): void {
    this.popupOrientation.stateMachine?.switchToState(
      Compatibility.isPortrait ? 'portrait' : 'landscape'
    );
  }

  private calculateHeight(gameRulesScene: ProgressBar, holderHeight: number): void {
    const markerText = gameRulesScene.findById<TextSceneObject>('gameUserInterfaceTitleLbl');

    if (!markerText) {
      throw new Error('marker text not found');
    }

    let lastChild = markerText.parent!.childs[0];

    markerText.parent!.childs.forEach((child) => {
      if (child.position.y > lastChild.position.y) {
        lastChild = child;
      }
    });

    const contentHeight = lastChild.position.y + lastChild.size.y;

    this.scrollableProgressBar.scrollableSize = new Vector2(0, -contentHeight + holderHeight);
  }

  private initGameRules(scene: SceneObject): void {
    const gameRulesScene = this.sceneFactory.build('bottom/sceneHelpContent') as ProgressBar;
    const rulesHolder = scene.findById<SceneObject>('rulesHolder');

    if (!rulesHolder) {
      throw new Error('rules holder not found');
    }

    if (!gameRulesScene) {
      throw new Error('game rules scene not found');
    }

    this.scrollableProgressBar = new ScrollableProgressBarComponent(gameRulesScene);

    this.scrollableProgressBar.initialize();

    gameRulesScene.initialize();

    rulesHolder.addChild(gameRulesScene);
  }

  private hideGameLines(): void {
    const line1 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'supplementaryInformationDescriptionLbl_7'
    );
    const line2 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'supplementaryInformationDescriptionLbl_8'
    );
    const line3 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'supplementaryInformationDescriptionLbl_9'
    );
    const line4 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'supplementaryInformationDescriptionLbl_10'
    );
    const line5 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'supplementaryInformationDescriptionLbl_11'
    );
    const line6 = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'supplementaryInformationDescriptionLbl_12'
    );

    line1?.parent!.removeChild(line1);
    line2?.parent!.removeChild(line2);
    line3?.parent!.removeChild(line3);
    line4?.parent!.removeChild(line4);
    line5?.parent!.removeChild(line5);
    line6?.parent!.removeChild(line6);
  }
}
